import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/ClerkAuthContext";
import { apiService } from "../../services/api";
import { useNavigate } from "react-router-dom";

interface Conversation {
  id: string;
  title: string;
  org: {
    id: string;
    slug: string;
    name: string;
  };
  app?: {
    id: string;
    slug: string;
    name: string;
  };
  message_count: number;
  created_at: string;
  updated_at: string;
}

interface EvalTemplate {
  name: string;
  description: string;
}

interface EvalResult {
  conversation_id: string;
  conversation_title: string;
  org_slug: string;
  summary: string;
  processing_time_ms: number;
  created_at: string;
}

export const ConversationEval: React.FC = () => {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [templates, setTemplates] = useState<EvalTemplate[]>([]);
  const [activeEval, setActiveEval] = useState<string | null>(null);
  const [evalResults, setEvalResults] = useState<EvalResult[]>([]);
  const [evalStatus, setEvalStatus] = useState<any>(null);

  // Load conversations
  const loadConversations = useCallback(async (cursor?: string | null) => {
    setLoading(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams();
      if (cursor) params.append("cursor", cursor);
      params.append("limit", "100");

      const response = await apiService.fetch(`/api/admin/conversations?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        if (cursor) {
          setConversations((prev) => [...prev, ...data.conversations]);
        } else {
          setConversations(data.conversations);
        }
        setNextCursor(data.next_cursor);
      }
    } catch (error) {
      console.error("Failed to load conversations:", error);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  // Load prompt templates
  const loadTemplates = useCallback(async () => {
    try {
      const token = await getToken();
      const response = await apiService.fetch("/api/admin/conversation-evals/prompts", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates);
      }
    } catch (error) {
      console.error("Failed to load templates:", error);
    }
  }, [getToken]);

  useEffect(() => {
    loadConversations();
    loadTemplates();
  }, [loadConversations, loadTemplates]);

  // Handle selection
  const handleSelectConversation = (id: string, selected: boolean) => {
    const newSelection = new Set(selectedIds);
    if (selected) {
      newSelection.add(id);
    } else {
      newSelection.delete(id);
    }
    setSelectedIds(newSelection);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(conversations.map((c) => c.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  // Start evaluation
  const handleStartEvaluation = async (config: any) => {
    try {
      const token = await getToken();
      const response = await apiService.fetch("/api/admin/conversation-evals", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          conversation_ids: Array.from(selectedIds),
          ...config,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setActiveEval(data.eval_id);
        setShowConfigDialog(false);
        setSelectedIds(new Set());
        // Redirect to the execution page
        navigate(`/admin/evals/${data.eval_id}/execute`);
      }
    } catch (error) {
      console.error("Failed to start evaluation:", error);
    }
  };

  // Poll evaluation status
  const pollEvalStatus = async (evalId: string) => {
    const token = await getToken();
    const interval = setInterval(async () => {
      try {
        const statusResponse = await apiService.fetch(
          `/api/admin/conversation-evals/${evalId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (statusResponse.ok) {
          const status = await statusResponse.json();
          setEvalStatus(status);

          if (status.status === "completed" || status.status === "failed") {
            clearInterval(interval);
            // Load results
            const resultsResponse = await apiService.fetch(
              `/api/admin/conversation-evals/${evalId}/results`,
              {
                headers: { Authorization: `Bearer ${token}` },
              }
            );
            if (resultsResponse.ok) {
              const results = await resultsResponse.json();
              setEvalResults(results.results);
            }
          }
        }
      } catch (error) {
        console.error("Error polling status:", error);
        clearInterval(interval);
      }
    }, 2000);
  };

  // Export results
  const handleExportResults = (format: "csv" | "json") => {
    if (format === "json") {
      const blob = new Blob([JSON.stringify(evalResults, null, 2)], {
        type: "application/json",
      });
      downloadBlob(blob, `evaluation-${activeEval}.json`);
    } else {
      // Convert to CSV
      const headers = ["Conversation ID", "Title", "Organization", "Summary", "Processing Time (ms)"];
      const rows = evalResults.map((r) => [
        r.conversation_id,
        r.conversation_title,
        r.org_slug,
        r.summary.replace(/"/g, '""'), // Escape quotes
        r.processing_time_ms.toString(),
      ]);

      const csv = [
        headers.join(","),
        ...rows.map((r) => r.map((v) => `"${v}"`).join(",")),
      ].join("\n");

      const blob = new Blob([csv], { type: "text/csv" });
      downloadBlob(blob, `evaluation-${activeEval}.csv`);
    }
  };

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">Conversation Evaluations</h2>

      {/* Evaluation in progress */}
      {activeEval && evalStatus && evalStatus.status !== "completed" && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-blue-900 mb-2">Evaluation in Progress</h3>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all"
                  style={{
                    width: `${
                      (evalStatus.completed_count / evalStatus.conversation_count) * 100
                    }%`,
                  }}
                />
              </div>
            </div>
            <span className="text-sm text-gray-600">
              {evalStatus.completed_count} / {evalStatus.conversation_count} complete
            </span>
          </div>
        </div>
      )}

      {/* Results view */}
      {evalResults.length > 0 ? (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Evaluation Results</h3>
            <div className="flex gap-2">
              <button
                onClick={() => handleExportResults("csv")}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                Export CSV
              </button>
              <button
                onClick={() => handleExportResults("json")}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Export JSON
              </button>
              <button
                onClick={() => {
                  setEvalResults([]);
                  setActiveEval(null);
                  setEvalStatus(null);
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                New Evaluation
              </button>
            </div>
          </div>

          <div className="space-y-4">
            {evalResults.map((result) => (
              <div
                key={result.conversation_id}
                className="bg-white border rounded-lg p-4"
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h4 className="font-semibold">{result.conversation_title}</h4>
                    <p className="text-sm text-gray-500">
                      {result.org_slug}
                    </p>
                  </div>
                  <span className="text-xs text-gray-400">
                    {result.processing_time_ms}ms
                  </span>
                </div>
                <p className="text-gray-700 whitespace-pre-wrap">{result.summary}</p>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <>
          {/* Selection bar */}
          {selectedIds.size > 0 && (
            <div className="bg-gray-100 border rounded-lg p-4 mb-4 flex justify-between items-center">
              <span>{selectedIds.size} conversations selected</span>
              <button
                onClick={() => setShowConfigDialog(true)}
                className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
              >
                Evaluate
              </button>
            </div>
          )}

          {/* Conversations table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3">
                    <input
                      type="checkbox"
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      checked={
                        conversations.length > 0 &&
                        selectedIds.size === conversations.length
                      }
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Organization
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    App
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Messages
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Created
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {conversations.map((conv) => (
                  <tr key={conv.id}>
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(conv.id)}
                        onChange={(e) =>
                          handleSelectConversation(conv.id, e.target.checked)
                        }
                      />
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {conv.title}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {conv.org.slug}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {conv.app?.name || "-"}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {conv.message_count}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(conv.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Load more */}
          {nextCursor && (
            <div className="mt-4 text-center">
              <button
                onClick={() => loadConversations(nextCursor)}
                disabled={loading}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50"
              >
                {loading ? "Loading..." : "Load More"}
              </button>
            </div>
          )}
        </>
      )}

      {/* Configuration Dialog */}
      {showConfigDialog && (
        <ConfigDialog
          templates={templates}
          onClose={() => setShowConfigDialog(false)}
          onSubmit={handleStartEvaluation}
          conversationCount={selectedIds.size}
        />
      )}
    </div>
  );
};

// Configuration Dialog Component
interface ConfigDialogProps {
  templates: EvalTemplate[];
  onClose: () => void;
  onSubmit: (config: any) => void;
  conversationCount: number;
}

const ConfigDialog: React.FC<ConfigDialogProps> = ({
  templates,
  onClose,
  onSubmit,
  conversationCount,
}) => {
  const [promptType, setPromptType] = useState<"template" | "custom">("template");
  const [selectedTemplate, setSelectedTemplate] = useState("eval_conversation_challenges");
  const [customPrompt, setCustomPrompt] = useState("");
  const [concurrentLimit, setConcurrentLimit] = useState(3);

  const handleSubmit = () => {
    const config: any = {
      concurrent_limit: concurrentLimit,
    };

    if (promptType === "template") {
      config.prompt_template = selectedTemplate;
    } else {
      config.custom_prompt = customPrompt;
    }

    onSubmit(config);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h3 className="text-lg font-semibold mb-4">Configure Evaluation</h3>
        <p className="text-sm text-gray-600 mb-4">
          Processing {conversationCount} conversations
        </p>

        <div className="space-y-4">
          {/* Prompt type selection */}
          <div>
            <label className="flex items-center mb-2">
              <input
                type="radio"
                value="template"
                checked={promptType === "template"}
                onChange={() => setPromptType("template")}
                className="mr-2"
              />
              Use Template
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                value="custom"
                checked={promptType === "custom"}
                onChange={() => setPromptType("custom")}
                className="mr-2"
              />
              Custom Prompt
            </label>
          </div>

          {/* Template or custom prompt */}
          {promptType === "template" ? (
            <select
              value={selectedTemplate}
              onChange={(e) => setSelectedTemplate(e.target.value)}
              className="w-full border rounded px-3 py-2"
            >
              {templates.map((template) => (
                <option key={template.name} value={template.name}>
                  {template.description}
                </option>
              ))}
            </select>
          ) : (
            <textarea
              value={customPrompt}
              onChange={(e) => setCustomPrompt(e.target.value)}
              placeholder="Enter your custom analysis prompt..."
              rows={6}
              className="w-full border rounded px-3 py-2"
            />
          )}

          {/* Concurrent limit */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Concurrent Processing Limit
            </label>
            <input
              type="number"
              min="1"
              max="10"
              value={concurrentLimit}
              onChange={(e) => setConcurrentLimit(Number(e.target.value))}
              className="w-full border rounded px-3 py-2"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={promptType === "custom" && !customPrompt}
            className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
          >
            Start Evaluation
          </button>
        </div>
      </div>
    </div>
  );
};